import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  TablePagination,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import queryString from 'query-string';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';

import { AdminUserSearchResult, BookingService, UserState } from 'us-web-services';
import useUser from '../../store/useUser';
import useAuthentication from '../../store/useAuthentication';
import LookupTabs from './LookupTabs';
import UserSearch from './UserSearch';
import DateUtil from '../../util/DateUtil';
import PageStyles from '../../styles/PageStyles';

const CARE_TYPES: Record<number, string> = {
  1: 'Child care',
  2: 'Pet care',
  3: 'Senior care',
  4: 'Household services',
};

const FILTER_TYPES = [
  { value: 'all_date', label: 'All (by date)' },
  { value: 'all_active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired_cancel', label: 'Expired/Cancelled' },
];

function UserBookings(props: RouteComponentProps) {
  const { classes } = PageStyles();
  const { history, location } = props;
  const userCache = useUser()[0];
  const authenticationState = useAuthentication()[0];
  const adminUserId = authenticationState.authentication.user.id;
  const searchParams = queryString.parse(location.search);
  const [userState, setUserState] = useState<UserState>({
    user: {},
    profile: {},
  });
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const urlFilterType = (searchParams.filterType as string) || 'all_date';
  const [filterType, setFilterType] = useState(urlFilterType);

  useEffect(() => {
    setUserState(userCache);
    if (searchParams.filterType) {
      setFilterType(searchParams.filterType as string);
    }
  }, [userCache, userCache.user.id, userCache.profile.id, searchParams.id, searchParams.filterType]);

  const isParent = !!userState.user.parent;

  useEffect(() => {
    const fetchBookings = async () => {
      if (!userState.user.id) return;

      setLoading(true);
      try {
        const roleParam = isParent
          ? { parentId: userState.user.id }
          : { sitterId: userState.user.id };

        const response = await BookingService.getByFilter({
          params: {
            filterType,
            page,
            ...roleParam,
            userId: adminUserId,
            minimalProperties: true,
          },
        });

        setBookings(response.data.data || []);
        setTotalCount(response.data.meta?.size || 0);
      } catch (error) {
        console.error('Failed to fetch bookings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [userState.user.id, adminUserId, page, filterType, isParent]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleFilterChange = (event: any) => {
    const newFilterType = event.target.value;

    setFilterType(newFilterType);
    setPage(1);

    // Update URL query params to maintain filter state
    const currentParams = queryString.parse(location.search);
    const newSearch = queryString.stringify({
      ...currentParams,
      filterType: newFilterType,
    });

    history.replace(`${location.pathname}?${newSearch}`);
  };

  const handleRowClick = (bookingId: number) => {
    history.push(`/bookings/lookup?id=${bookingId}`);
  };

  const handleSearch = (term: string) => {
    history.push({ pathname: '/users/lookup', search: `?search=${encodeURIComponent(term)}` });
  };

  const handleSelectUser = (result: AdminUserSearchResult) => {
    if (result.id) {
      history.push({ pathname: '/users/bookings', search: `?id=${result.id}` });
    }
  };

  return (
    <Paper className={classes.paper}>
      <AppBar position='static' color='transparent' elevation={0}>
        <Toolbar>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs>
              <UserSearch
                onSearch={handleSearch}
                onSelectUser={handleSelectUser}
              />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <div style={{ margin: '40px 16px' }}>
        {userState.user.id && userState.profile.id ? (
          <>
            <LookupTabs user={userState.user} />
            <Box sx={{ mb: 2, mt: 2 }}>
              <FormControl size='small' sx={{ minWidth: 200 }}>
                <InputLabel>Filter</InputLabel>
                <Select
                  value={filterType}
                  label='Filter'
                  onChange={handleFilterChange}
                >
                  {FILTER_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {loading ? (
              <Box display='flex' justifyContent='center' p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>{isParent ? 'Sitter' : 'Parent'}</TableCell>
                      <TableCell>Care Type</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Payment Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookings.map(booking => (
                      <TableRow
                        key={booking.id}
                        hover
                        onClick={() => handleRowClick(booking.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          {DateUtil.getReadableDateTime(booking.start)}
                        </TableCell>
                        <TableCell>
                          {DateUtil.getReadableDateTime(booking.end)}
                        </TableCell>
                        <TableCell>{booking.status?.code || ''}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {isParent ? (
                            booking.sitter?.id ? (
                              <Link to={`/users/lookup?id=${booking.sitter.id}`}>
                                {`${booking.sitter.firstName || ''} ${booking.sitter.lastName || ''}`.trim()
                                  || booking.sitter.id}
                              </Link>
                            ) : ''
                          ) : (
                            booking.parent?.id ? (
                              <Link to={`/users/lookup?id=${booking.parent.id}`}>
                                {booking.parent.name || booking.parent.id}
                              </Link>
                            ) : ''
                          )}
                        </TableCell>
                        <TableCell>
                          {booking.typeId ? CARE_TYPES[booking.typeId] : ''}
                        </TableCell>
                        <TableCell>
                          {booking.duration ? `${booking.duration} hrs` : ''}
                        </TableCell>
                        <TableCell>{booking.paymentType || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component='div'
                  count={totalCount}
                  page={page - 1}
                  onPageChange={handleChangePage}
                  rowsPerPage={10}
                  rowsPerPageOptions={[10]}
                />
              </TableContainer>
            )}
          </>
        ) : (
          <Typography
            color='textPrimary'
            align='center'
            component='h5'
            variant='h5'
          >
            No user found
          </Typography>
        )}
      </div>
    </Paper>
  );
}

export default withRouter(UserBookings);
